import { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import type { Assistant, StructuredOutputField } from "../api";
import { useAssistantAnalysis, useUpdateAssistant } from "../hooks";

function fmtMinutes(seconds: number) {
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return m ? `${m}m ${s}s` : `${s}s`;
}

/** Analysis tab: this assistant's numbers + the structured-output fields the
 * AI extracts from every call. Stats are read-only aggregation; the fields
 * editor PATCHes assistant.structured_outputs. */
export default function AssistantAnalysis({ assistant }: { assistant: Assistant }) {
  const q = useAssistantAnalysis(assistant.id);
  const a = q.data;

  return (
    <div className="space-y-6">
      {/* ── Overview ── */}
      {q.isLoading ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </div>
      ) : q.isError || !a ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            Couldn't load the stats.
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatTile label="Total calls" value={String(a.total_calls)} />
            <StatTile
              label="Completed"
              value={
                a.total_calls
                  ? `${a.completed} (${Math.round((a.completed / a.total_calls) * 100)}%)`
                  : "0"
              }
            />
            <StatTile label="Failed" value={String(a.failed)} />
            <StatTile label="Avg duration" value={fmtMinutes(a.avg_seconds)} />
            <StatTile label="Talk time" value={fmtMinutes(a.total_seconds)} />
            <StatTile
              label="Web / Phone"
              value={`${a.web_calls} / ${a.phone_calls}`}
            />
            <StatTile
              label="Inbound / Outbound"
              value={`${a.inbound} / ${a.outbound}`}
            />
            <StatTile
              label="Avg lead score"
              value={
                a.avg_lead_score != null
                  ? `${a.avg_lead_score} (${a.scored_calls} scored)`
                  : "—"
              }
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Calls — last 14 days</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-52">
                  {a.daily.length === 0 ? (
                    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                      No calls in the last 14 days.
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={a.daily}>
                        <CartesianGrid strokeDasharray="3 3" opacity={0.4} />
                        <XAxis
                          dataKey="day"
                          tick={{ fontSize: 11 }}
                          tickFormatter={(d: string) => d.slice(5)}
                        />
                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                        <Tooltip
                          formatter={(v) => [v, "Calls"]}
                          labelFormatter={(d) => String(d)}
                        />
                        <Bar
                          dataKey="calls"
                          name="Calls"
                          fill="#8b5cf6"
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Sentiment</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <SentimentRow
                  label="Positive"
                  count={a.sentiment.positive}
                  total={a.total_calls}
                  barCls="bg-emerald-500"
                />
                <SentimentRow
                  label="Neutral"
                  count={a.sentiment.neutral}
                  total={a.total_calls}
                  barCls="bg-slate-400"
                />
                <SentimentRow
                  label="Negative"
                  count={a.sentiment.negative}
                  total={a.total_calls}
                  barCls="bg-rose-500"
                />
                <SentimentRow
                  label="Not analyzed"
                  count={a.sentiment.unanalyzed}
                  total={a.total_calls}
                  barCls="bg-muted-foreground/30"
                />
              </CardContent>
            </Card>
          </div>
        </>
      )}

      <StructuredOutputsEditor assistant={assistant} />
    </div>
  );
}

function StatTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border bg-card px-4 py-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-xl font-semibold tabular-nums">{value}</p>
    </div>
  );
}

function SentimentRow({
  label,
  count,
  total,
  barCls,
}: {
  label: string;
  count: number;
  total: number;
  barCls: string;
}) {
  const pct = total ? Math.round((count / total) * 100) : 0;
  return (
    <div className="flex items-center gap-3 text-sm">
      <span className="w-24 shrink-0 text-muted-foreground">{label}</span>
      <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
        <div className={cn("h-full rounded-full", barCls)} style={{ width: `${pct}%` }} />
      </div>
      <span className="w-16 shrink-0 text-right tabular-nums text-muted-foreground">
        {count} · {pct}%
      </span>
    </div>
  );
}

// ── Structured Outputs ──────────────────────────────────────────────────────

const TYPE_OPTIONS: { value: StructuredOutputField["type"]; label: string }[] = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "boolean", label: "Yes / No" },
];

function keyFromLabel(label: string) {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 60);
}

function StructuredOutputsEditor({ assistant }: { assistant: Assistant }) {
  const [fields, setFields] = useState<StructuredOutputField[]>(
    assistant.structured_outputs ?? [],
  );
  const [dirty, setDirty] = useState(false);
  const updateMut = useUpdateAssistant(assistant.id);

  // Adopt server changes (e.g. after a restore) unless the user is editing.
  useEffect(() => {
    if (!dirty) setFields(assistant.structured_outputs ?? []);
  }, [assistant.structured_outputs, dirty]);

  function patch(i: number, part: Partial<StructuredOutputField>) {
    setFields((prev) => prev.map((f, idx) => (idx === i ? { ...f, ...part } : f)));
    setDirty(true);
  }

  function save() {
    const cleaned = fields
      .filter((f) => f.label.trim())
      .map((f) => ({ ...f, key: f.key || keyFromLabel(f.label) }));
    updateMut.mutate(
      { structured_outputs: cleaned },
      {
        onSuccess: () => {
          setDirty(false);
          toast.success("Structured outputs saved");
        },
        onError: () => toast.error("Couldn't save the fields."),
      },
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between space-y-0">
        <div>
          <CardTitle className="text-base">Structured Outputs</CardTitle>
          <CardDescription>
            The AI extracts these fields from every call. Values appear in the
            call details (Logs tab → open a call).
          </CardDescription>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setFields((prev) => [
              ...prev,
              { key: "", label: "", type: "text", description: "" },
            ]);
            setDirty(true);
          }}
        >
          <Plus className="size-4" />
          Add Field
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {fields.length === 0 && (
          <p className="py-6 text-center text-sm text-muted-foreground">
            No fields yet. Add one — e.g. “Booking status”, “Customer name”,
            “Callback requested”.
          </p>
        )}
        {fields.map((f, i) => (
          <div
            key={i}
            className="grid gap-2 rounded-lg border p-3 sm:grid-cols-[1fr_140px_1fr_auto]"
          >
            <Input
              value={f.label}
              placeholder="Field name (e.g. Booking status)"
              onChange={(e) =>
                patch(i, {
                  label: e.target.value,
                  key: keyFromLabel(e.target.value),
                })
              }
            />
            <Select
              value={f.type}
              onValueChange={(v) =>
                patch(i, { type: v as StructuredOutputField["type"] })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TYPE_OPTIONS.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              value={f.description}
              placeholder="Hint for the AI (optional)"
              onChange={(e) => patch(i, { description: e.target.value })}
            />
            <Button
              variant="ghost"
              size="icon"
              className="size-9 text-destructive hover:text-destructive"
              onClick={() => {
                setFields((prev) => prev.filter((_, idx) => idx !== i));
                setDirty(true);
              }}
              aria-label="Remove field"
            >
              <Trash2 className="size-4" />
            </Button>
          </div>
        ))}
        <div className="flex justify-end">
          <Button
            size="sm"
            disabled={!dirty || updateMut.isPending}
            onClick={save}
          >
            {updateMut.isPending && <Loader2 className="size-4 animate-spin" />}
            Save Fields
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
